import { useEffect, useRef, useState } from 'react';

/**
 * Carousel animation phase.
 *
 * stable   — normal rendering, visible pair reflects live timing data
 * hold     — 2 s: the prise d'antenne just hit 0 ; we show the outgoing
 *            PRISE frozen at 00:00:00 on the left and the freshly-activated
 *            RENDU on the right
 * swiping  — 1 s: the ribbon translates left by one slot (CSS transition).
 *            The outgoing PRISE scrolls off-screen and the new RENDU
 *            becomes the left card.
 */
export type CarouselPhase = 'stable' | 'hold' | 'swiping';

export const CAROUSEL_HOLD_MS = 2_000;
export const CAROUSEL_SWIPE_MS = 1_000;

export interface CarouselState<TSnap> {
  phase: CarouselPhase;
  snapshot: TSnap | null;
}

/**
 * Orchestrates the hold → swiping → stable state machine triggered when
 * ANY of the provided countdowns transitions from > 0 to 0.
 *
 *   stable ──(count: 1→0)──▶ hold ──(+2 s)──▶ swiping ──(+N s)──▶ stable
 *
 * Two countdowns are watched :
 *   - `priseCountdown` : time until the next live starts
 *   - `renduCountdown` : time until the current live ends
 * Either one hitting 0 fires the animation, so we get a swipe both when
 * a PRISE activates a live AND when a current live ends with a PRISE
 * already queued (non-back-to-back case).
 *
 * The caller provides a `makeSnapshot` factory invoked at trigger time.
 * The returned snapshot is frozen and held for the duration of hold+
 * swiping, then cleared back to null when the animation ends.
 *
 * The optional `getSwipeMs` lets the caller scale the swiping phase — for
 * a 2-slot shift we want constant velocity, so twice the duration.
 */
export function useCarouselPhase<TSnap>(
  priseCountdown: number | undefined,
  renduCountdown: number | undefined,
  makeSnapshot: () => TSnap,
  getSwipeMs?: (snapshot: TSnap) => number,
): CarouselState<TSnap> {
  const [state, setState] = useState<CarouselState<TSnap>>({
    phase: 'stable',
    snapshot: null,
  });

  const makeSnapshotRef = useRef(makeSnapshot);
  makeSnapshotRef.current = makeSnapshot;
  const getSwipeMsRef = useRef(getSwipeMs);
  getSwipeMsRef.current = getSwipeMs;

  const prevPriseRef = useRef<number | undefined>(undefined);
  const prevRenduRef = useRef<number | undefined>(undefined);
  const runningRef = useRef(false);
  const timerARef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timerBRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const prevPrise = prevPriseRef.current;
    const prevRendu = prevRenduRef.current;
    const currPrise = priseCountdown;
    const currRendu = renduCountdown;

    const priseHit = prevPrise !== undefined && prevPrise > 0 && currPrise === 0;
    const renduHit = prevRendu !== undefined && prevRendu > 0 && currRendu === 0;

    if (!runningRef.current && (priseHit || renduHit)) {
      runningRef.current = true;
      const snapshot = makeSnapshotRef.current();
      const swipeMs = getSwipeMsRef.current?.(snapshot) ?? CAROUSEL_SWIPE_MS;
      setState({ phase: 'hold', snapshot });
      timerARef.current = setTimeout(() => {
        setState((s) => ({ ...s, phase: 'swiping' }));
        timerBRef.current = setTimeout(() => {
          setState({ phase: 'stable', snapshot: null });
          runningRef.current = false;
        }, swipeMs);
      }, CAROUSEL_HOLD_MS);
    }

    prevPriseRef.current = currPrise;
    prevRenduRef.current = currRendu;
  }, [priseCountdown, renduCountdown]);

  useEffect(() => {
    return () => {
      if (timerARef.current) clearTimeout(timerARef.current);
      if (timerBRef.current) clearTimeout(timerBRef.current);
    };
  }, []);

  return state;
}
